/**
 * Multi-tab coordination primitives.
 *
 * Two problems this solves (both root causes of session fragmentation):
 *
 * 1. Parallel session minting — two tabs starting (or renewing) at the same
 *    time each handshake with no/stale session ID and the server mints one
 *    session per tab. `withSessionLock` serializes those critical sections
 *    across tabs via the Web Locks API; whichever tab wins the lock is the
 *    leader for that handshake, and the losers re-read localStorage under the
 *    lock and adopt the winner's ID instead of minting their own.
 *
 * 2. Premature session end — closing ONE tab fired a `finishedSession`
 *    beacon even though other tabs were still using the same session, so the
 *    server stamped endTime mid-session. `TabRegistry` keeps a heartbeat
 *    roster of live tabs in localStorage; only the last live tab may finish
 *    the session.
 *
 * Where Web Locks is unavailable (very old browsers, non-window contexts)
 * `withSessionLock` degrades to an uncoordinated direct call — exactly the
 * pre-existing behavior, never worse.
 */

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const TABS_STORAGE_KEY = "jstverify_tabs";
export const SESSION_LOCK_NAME = "jstverify_session_handshake";
/** Bound on acquiring the cross-tab lock. Waiters that starve (hung holder)
 * degrade to an uncoordinated call rather than wedging every tab forever. */
export const SESSION_LOCK_TIMEOUT_MS = 8_000;

/**
 * Heartbeat cadence and staleness threshold. Hidden tabs get their timers
 * throttled hard (Chrome's intensive throttling runs timers about once per
 * minute after 5 minutes hidden), so the stale threshold must comfortably
 * exceed one minute or an idle-but-open background tab would be declared
 * dead and the session finished underneath it.
 */
export const TAB_HEARTBEAT_INTERVAL_MS = 20_000;
export const TAB_STALE_MS = 90_000;

function generateTabId(): string {
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Roster of live tabs sharing this origin's session, stored as a single JSON
 * object { tabId: lastHeartbeatMs } under TABS_STORAGE_KEY.
 *
 * The tab ID is persisted in sessionStorage, which is per-tab and survives
 * same-tab navigations — so a tab navigating between pages keeps one roster
 * entry instead of appearing to close and reopen.
 *
 * Concurrent writes can race (localStorage has no CAS). Heartbeat entries are
 * self-healing while tabs stay open (each re-asserts on the next beat; stale
 * entries prune on every read). Simultaneous close is different: two tabs can
 * each unregister and each observe the other as still live, so neither sends
 * the finish beacon. That path is best-effort — the server idle sweeper
 * (#196) is the guarantee that endTime is eventually stamped. Do not treat
 * TabRegistry alone as a closed-session invariant.
 */
export class TabRegistry {
  private readonly tabId: string;

  constructor(
    private readonly storage: StorageLike,
    tabId?: string,
    private readonly now: () => number = Date.now,
  ) {
    this.tabId = tabId || generateTabId();
  }

  /** Restore (or mint) the per-tab ID from sessionStorage. */
  static persistentTabId(sessionStorage: StorageLike): string {
    try {
      const existing = sessionStorage.getItem("jstverify_tab_id");
      if (existing) return existing;
      const fresh = generateTabId();
      sessionStorage.setItem("jstverify_tab_id", fresh);
      return fresh;
    } catch {
      return generateTabId();
    }
  }

  getTabId(): string {
    return this.tabId;
  }

  private read(): Record<string, number> {
    try {
      const raw = this.storage.getItem(TABS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return {};
      }
      const out: Record<string, number> = {};
      for (const key of Object.keys(parsed)) {
        const value = Number(parsed[key]);
        if (Number.isFinite(value)) out[key] = value;
      }
      return out;
    } catch {
      return {};
    }
  }

  private write(tabs: Record<string, number>): void {
    try {
      if (Object.keys(tabs).length === 0) {
        this.storage.removeItem(TABS_STORAGE_KEY);
      } else {
        this.storage.setItem(TABS_STORAGE_KEY, JSON.stringify(tabs));
      }
    } catch {
      // Storage full/unavailable — coordination degrades, tracking continues.
    }
  }

  private prune(tabs: Record<string, number>): Record<string, number> {
    const cutoff = this.now() - TAB_STALE_MS;
    const out: Record<string, number> = {};
    for (const key of Object.keys(tabs)) {
      if (tabs[key] > cutoff) out[key] = tabs[key];
    }
    return out;
  }

  /** Assert this tab is alive; prunes stale entries as a side effect. */
  beat(): void {
    const tabs = this.prune(this.read());
    tabs[this.tabId] = this.now();
    this.write(tabs);
  }

  /** Remove this tab from the roster (page closing/navigating away). */
  unregister(): void {
    const tabs = this.read();
    delete tabs[this.tabId];
    this.write(tabs);
  }

  /**
   * True when no OTHER live tab exists. The session-end beacon must only
   * fire from the last live tab; anything else ends a session other tabs are
   * still feeding.
   */
  isLastLiveTab(): boolean {
    const tabs = this.prune(this.read());
    return Object.keys(tabs).every((id) => id === this.tabId);
  }
}

type LockManagerLike = {
  request: (
    name: string,
    options: { mode: "exclusive"; signal?: AbortSignal },
    callback: () => Promise<unknown>,
  ) => Promise<unknown>;
};

function makeTimeoutSignal(ms: number): AbortSignal | undefined {
  try {
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
      return AbortSignal.timeout(ms);
    }
  } catch {
    // AbortSignal.timeout unavailable or threw — fall through.
  }
  if (typeof AbortController === "undefined") return undefined;
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name: string }).name === "AbortError"
  );
}

function getLockManager(): LockManagerLike | null {
  if (typeof navigator === "undefined") return null;
  const locks = (navigator as { locks?: unknown }).locks;
  if (
    locks &&
    typeof (locks as LockManagerLike).request === "function"
  ) {
    return locks as LockManagerLike;
  }
  return null;
}

export function hasWebLocks(): boolean {
  return getLockManager() !== null;
}

/**
 * Run fn while holding the cross-tab session lock. Session handshakes and
 * rotations must go through here: the lock holder is the leader for that
 * operation, and waiters re-check localStorage after acquiring so they adopt
 * a session the leader just minted instead of minting another.
 */
export async function withSessionLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = getLockManager();
  if (!locks) return fn();
  const signal = makeTimeoutSignal(SESSION_LOCK_TIMEOUT_MS);
  try {
    return (await locks.request(
      SESSION_LOCK_NAME,
      signal ? { mode: "exclusive", signal } : { mode: "exclusive" },
      fn,
    )) as T;
  } catch (err) {
    // Acquisition timed out (hung holder or starved waiter). Degrade to the
    // no-locks path so init/rotation cannot wedge every tab on the origin.
    if (signal && isAbortError(err)) return fn();
    throw err;
  }
}
